import {addPcbNodes} from "@buildwithflux/core";
import {
    AutoLayoutStatus,
    type AliffBoard,
    type AutoLayoutJob,
    type AutoLayoutJobMetadata,
    type AutoLayoutJobUid,
    type NodeUid,
} from "@buildwithflux/models";
import type {AutoLayoutJobMetadataRepository, AutoLayoutJobRepository} from "@buildwithflux/repositories";
import {pickValues, type Logger, type Unsubscriber} from "@buildwithflux/shared";

import type {DocumentStore} from "modules/document/state/document";
import type {UseDocumentMetaStore} from "modules/documentMeta/state/documentMeta";
import type {CurrentUserService} from "modules/auth";
import type {UseFeatureFlagsStore} from "modules/feature_flags/FeatureFlagsStore";
import {selectAutoLayoutJobConfiguration} from "modules/feature_flags/hooks";
import type {ActionBakeObserver} from "modules/observers/ActionBakeObserver";
import {AbstractDocumentPreviewService} from "modules/preview/DocumentPreviewService";
import type {AnalyticsStorage} from "modules/storage_engine/AnalyticsStorage";
import {trackAutoLayoutEvent} from "modules/auto_layout/hooks";
import {createNodesFromExistingAutoLayoutNodes, getAutoLayoutNodeUidsInDocument} from "modules/auto_layout/utils";
import {isTrivialJobChange} from "modules/auto_layout/utils/jobComparison";
import {buildNetPropertyUpdates} from "modules/auto_layout/utils/netPropertyUtils";

import type {AutoLayoutApiClient} from "./AutoLayoutApiClient";
import type {AutoLayoutBoardExporter} from "./AutoLayoutBoardExporter";
import type {AutoLayoutEventBus} from "./AutoLayoutEventBus";
import type {AutoLayoutIterationProcessor} from "./AutoLayoutIterationProcessor";
import type {AutoLayoutReviewService} from "./AutoLayoutReviewService";
import type {AutoLayoutService} from "./AutoLayoutService";
import {AutoLayoutClientJobState, type AutoLayoutStoreService} from "./AutoLayoutStoreService";
import {DeepPcbAutoLayoutBaseService} from "./DeepPcbAutoLayoutBaseService";
import type {DeepPcbBoardExportMetadata} from "./DeepPcbAutoLayoutBoardExporter";
import type {DeepPcbAutoLayoutEventMap} from "./DeepPcbAutoLayoutEventBus";
import {current} from "mutative";

/**
 * DeepPCB implementation of the auto-layout orchestration service.
 * Coordinates all auto-layout operations using the extracted services.
 * Uses event bus to notify hooks about job changes.
 */
export class DeepPcbAutoLayoutService extends DeepPcbAutoLayoutBaseService implements AutoLayoutService {
    private subscriptions: Unsubscriber[] = [];
    private jobSubscription: Unsubscriber | undefined;
    private metadataSubscription: Unsubscriber | undefined;

    constructor(
        eventBus: AutoLayoutEventBus<DeepPcbAutoLayoutEventMap>,
        logger: Logger,
        private readonly autoLayoutStoreService: AutoLayoutStoreService,
        private readonly apiClient: AutoLayoutApiClient,
        private readonly boardExporter: AutoLayoutBoardExporter<AliffBoard, DeepPcbBoardExportMetadata>,
        private readonly iterationProcessor: AutoLayoutIterationProcessor,
        private readonly reviewService: AutoLayoutReviewService,
        private readonly autoLayoutJobRepository: AutoLayoutJobRepository,
        private readonly autoLayoutJobMetadataRepository: AutoLayoutJobMetadataRepository,
        private readonly actionBakeObserver: ActionBakeObserver,
        private readonly documentPreviewService: AbstractDocumentPreviewService,
        private readonly currentUserService: CurrentUserService,
        private readonly useFeatureFlagsStore: UseFeatureFlagsStore,
        private readonly analyticsStorage: AnalyticsStorage,
        private readonly useDocumentMetaStore: UseDocumentMetaStore,
        private readonly documentStore: DocumentStore,
    ) {
        super("AutoLayoutService", eventBus, logger);
    }

    /**
     * Starts a new auto-layout job
     * @param layoutNodeUid - The UID of the layout node to run auto-layout on
     * @param organizationUid - Optional organization UID for the job
     * @throws {Error} When project UID is not found or job creation fails
     */
    public startJob(layoutNodeUid: NodeUid): Promise<void> {
        const runTask = this.createTaskRunner("start auto-layout job", {
            onError: () => this.cleanup({clearIteration: true}),
        });

        return runTask(async () => {
            // Use Zustand store for non-reactive access to document state
            const documentState = this.documentStore.getState().document;

            const projectUid = documentState?.uid;
            if (!projectUid) {
                throw new Error("Project UID not found");
            }

            const currentUser = this.currentUserService.getCurrentUser();
            if (!currentUser) {
                throw new Error("Current user not found");
            }

            const organizationUid = documentState?.organization_owner_uid;

            this.logger.info("Starting auto-layout job", {projectUid, layoutNodeUid, organizationUid});
            this.emit("job.starting", {projectUid, layoutNodeUid, organizationUid});

            const jobConfig = selectAutoLayoutJobConfiguration(this.useFeatureFlagsStore.getState());

            // Step 1: Create client job immediately
            this.autoLayoutStoreService.startJob(
                projectUid,
                currentUser.uid,
                organizationUid,
                layoutNodeUid,
                jobConfig,
            );
            // Set initial state to waiting
            this.autoLayoutStoreService.setState(AutoLayoutClientJobState.waiting);

            // Step 2: Create job via API
            const {jobUid, uploadUrl} = await this.apiClient.createJob(projectUid, currentUser.uid, organizationUid);
            this.autoLayoutStoreService.setJobUid(jobUid);
            this.logger.debug("Job created successfully", {jobUid});
            this.emit("job.created", {jobUid, uploadUrl});

            // Step 3: Set up all subscriptions (job, metadata, and event)
            this.setupSubscriptions(jobUid, currentUser.handle);

            // Step 4: Optional net classification. This step has to complete _before_ exporting the board
            // since we need the updated net types to generate the routing priorities.
            await this.classifyAndUpdateNetTypes();

            // Step 5: Export board data
            const {boardString} = this.boardExporter.export(layoutNodeUid);

            // Step 6: Populate the firestore job by uploading board data
            await this.apiClient.populateJob({
                jobUid,
                boardData: boardString,
                uploadUrl,
            });

            this.logger.info("Auto-layout job populated successfully", {jobUid});
            this.emit("job.populate.completed", {jobUid});
        });
    }

    /**
     * Loads any active auto-layout job for the given layout node
     * @param layoutNodeUid - The UID of the layout node to load job for
     * @throws {Error} When job loading fails
     */
    public loadJob(layoutNodeUid: string): Promise<void> {
        const runTask = this.createTaskRunner("load active job");

        return runTask(async () => {
            const projectUid = this.documentStore.getState().document?.uid;
            if (!projectUid) {
                throw new Error("Project UID not found");
            }

            const currentUser = this.currentUserService.getCurrentUser();
            if (!currentUser) {
                throw new Error("Current user not found");
            }

            this.emit("job.loading", {layoutNodeUid});

            const activeJob = await this.autoLayoutJobRepository.getActiveJobForProject(
                currentUser.handle,
                currentUser.uid,
                projectUid,
            );
            if (!activeJob) {
                this.logger.debug("No active job found for project", {projectUid});
                return;
            }

            this.logger.info("Found active job", {jobUid: activeJob.jobUid});

            // Use store service to create job from backend job
            this.autoLayoutStoreService.loadFromBackendJob(activeJob, layoutNodeUid);

            this.logger.info("Loaded existing active job", {jobUid: activeJob.jobUid});

            // Set up all subscriptions (job, metadata, and event)
            this.setupSubscriptions(activeJob.jobUid, currentUser.handle);

            // Handle the loaded job directly
            await this.handleJobUpdates(activeJob);

            // Emit event that job was loaded
            this.emit("job.loaded", {jobUid: activeJob.jobUid, backendJob: activeJob});
        });
    }

    /**
     * Pauses the current auto-layout job
     * @throws {Error} When no active job exists or pause operation fails
     */
    public pauseJob(): Promise<void> {
        const runTask = this.createTaskRunner("pause job", {resetJobStateOnError: true});

        return runTask(async () => {
            const currentJob = this.autoLayoutStoreService.getCurrentJob();
            if (!currentJob?.jobUid) {
                throw new Error("No active job to pause");
            }

            this.emit("job.pausing", {});

            // Optimistic update to pausing state (like legacy)
            this.autoLayoutStoreService.setState(AutoLayoutClientJobState.pausing);

            await this.apiClient.pauseJob({
                jobUid: currentJob.jobUid,
                userUid: currentJob.userUid,
                organizationUid: currentJob.organizationUid,
            });

            this.logger.info("Job paused successfully", {jobUid: currentJob.jobUid});
            this.emit("job.paused", {jobUid: currentJob.jobUid});
        });
    }

    /**
     * Resumes the current auto-layout job
     * @throws {Error} When no active job exists or resume operation fails
     */
    public resumeJob(): Promise<void> {
        const runTask = this.createTaskRunner("resume job", {resetJobStateOnError: true});

        return runTask(async () => {
            this.emit("job.resuming", {});

            const currentJob = this.autoLayoutStoreService.getCurrentJob();
            if (!currentJob?.jobUid) {
                throw new Error("No active job to resume");
            }

            // Optimistic update to resuming state (like legacy)
            this.autoLayoutStoreService.setState(AutoLayoutClientJobState.resuming);

            await this.apiClient.resumeJob({
                jobUid: currentJob.jobUid,
                userUid: currentJob.userUid,
                organizationUid: currentJob.organizationUid,
            });

            this.logger.info("Job resumed successfully", {jobUid: currentJob.jobUid});
            this.emit("job.resumed", {jobUid: currentJob.jobUid});
        });
    }

    /**
     * Cancels the current auto-layout job
     * @throws {Error} When no active job exists or cancel operation fails
     */
    public cancelJob(): Promise<void> {
        const runTask = this.createTaskRunner("cancel job", {resetJobStateOnError: true});
        return runTask(async () => {
            const currentJob = this.autoLayoutStoreService.getCurrentJob();
            if (!currentJob) {
                throw new Error("No job to cancel");
            }

            this.emit("job.cancelling", {jobUid: currentJob.jobUid});

            // Optimistic update to canceling state (like legacy)
            this.autoLayoutStoreService.setState(AutoLayoutClientJobState.canceling);

            // If there's a jobUid, means there's a firestore/backend job to cancel
            // Otherwise, it means the job was cancelled before it was created in backend
            if (currentJob.jobUid) {
                await this.apiClient.cancelJob({
                    jobUid: currentJob.jobUid,
                    userUid: currentJob.userUid,
                    organizationUid: currentJob.organizationUid,
                });
            }

            this.emit("job.canceled", {jobUid: currentJob.jobUid});

            this.cleanup({clearIteration: true});

            this.logger.info("Job canceled successfully", {jobUid: currentJob.jobUid});
        });
    }

    /**
     * Applies the current iteration of the auto-layout job
     * @throws {Error} When no active job exists or apply operation fails
     */
    public applyCurrentIteration(): Promise<void> {
        const runTask = this.createTaskRunner("apply job", {resetJobStateOnError: true});

        return runTask(async () => {
            const currentJob = this.autoLayoutStoreService.getCurrentJob();
            if (!currentJob?.jobUid) {
                throw new Error("No active job to apply");
            }

            this.emit("iteration.applying", {});

            // Optimistic update to applying state (like legacy)
            this.autoLayoutStoreService.setState(AutoLayoutClientJobState.applying);

            // Dispatch action to persist and bake the current iteration
            await this.documentPreviewService.applyPreview((draftState) => {
                const currentNodeUids = getAutoLayoutNodeUidsInDocument(draftState);
                const currentNodes = pickValues(draftState.pcbLayoutNodes, currentNodeUids);
                const nodesToAdd = createNodesFromExistingAutoLayoutNodes(
                    currentNodes.map((draftNode) => current(draftNode)),
                );

                addPcbNodes(draftState.pcbLayoutNodes, nodesToAdd);
            });
            this.emit("iteration.baked", {jobUid: currentJob.jobUid});

            // Apply the backend job
            await this.apiClient.applyJob({
                jobUid: currentJob.jobUid,
                userUid: currentJob.userUid,
                organizationUid: currentJob.organizationUid,
                iterationIndex: currentJob.currentIterationIndex,
            });

            this.cleanup();

            this.logger.info("Job applied successfully", {jobUid: currentJob.jobUid});
        });
    }

    /**
     * Saves the selected iteration index and persists it to backend
     * @param iterationIndex - The index of the iteration to select
     * @throws {Error} When no active job exists, invalid iteration index, or save operation fails
     */
    public selectIteration(iterationIndex: number): Promise<void> {
        const runTask = this.createTaskRunner("save iteration selection");

        return runTask(async () => {
            const currentJob = this.autoLayoutStoreService.getCurrentJob();
            if (!currentJob?.jobUid) {
                throw new Error("No active job to save iteration for");
            }

            this.emit("iteration.selecting", {iterationIndex});

            this.logger.debug("Saving selected iteration", {iterationIndex});

            // Immediately update the store with the selected iteration
            this.autoLayoutStoreService.setSliderPosition(iterationIndex);

            // Start rendering the selected iteration
            const renderedIteration = await this.iterationProcessor.renderIteration(iterationIndex);

            // The iteration might not be rendered cuz of a few conditions:
            // - The iteration is not found (out of bounds, not yet received from backend)
            // - The iteration is already rendered
            // - The slider position has changed while we were rendering
            // - Other general null checks or errors
            if (!renderedIteration) {
                this.logger.debug("Skipping rendering for selected iteration", {
                    jobUid: currentJob.jobUid,
                    iterationIndex,
                });
                return;
            }

            trackAutoLayoutEvent(this.analyticsStorage, "changeIteration", currentJob);

            // Handle backend persistence
            const currentUser = this.currentUserService.getCurrentUser();
            if (!currentUser) {
                throw new Error("Current user not found");
            }

            await this.autoLayoutJobRepository.saveSelectedIteration(
                currentUser.handle,
                currentJob.jobUid,
                renderedIteration.hash,
            );

            this.logger.info("Iteration selection saved successfully", {
                jobUid: currentJob.jobUid,
                iterationIndex,
                iterationHash: renderedIteration.hash,
            });

            // Emit iteration selected event
            this.emit("iteration.selected", {
                jobUid: currentJob.jobUid,
                iterationIndex,
                iterationHash: currentJob.iterations[iterationIndex]?.hash || "",
            });
        });
    }

    /**
     * Shuts down the service and cleans up resources
     */
    public shutdown(): void {
        this.emit("service.shutting_down", {});
        this.logger.debug("Shutting down auto-layout coordinator");
        this.cleanup();
    }

    /**
     * Cleans up current job and subscriptions
     *
     * Note:
     * We don't cleanup/unsubscribe the event bus subscriptions, we let the
     * subscribers take care of unsubscribing themselves.
     * A lot of the UI components subscribe to the event bus, and we want them
     * to continue to listen for events even after the job is cleaned up.
     */
    private cleanup(options?: {clearIteration?: boolean}): void {
        if (options?.clearIteration) {
            this.documentPreviewService.clearPreview();
        }

        this.cleanupSubscriptions();

        this.autoLayoutStoreService.resetStore();

        this.iterationProcessor.reset();

        this.emit("job.cleanedUp", {
            jobUid: this.autoLayoutStoreService.getCurrentJob()?.jobUid,
        });
    }

    /**
     * Creates a task runner configured with error handling, logging, and event emission
     * @param label - A descriptive label for the task, used in logging and error messages
     * @param options - Configuration for the task runner
     * @returns A function that executes tasks with the configured error handling
     */
    private createTaskRunner<T = void>(
        label: string,
        options?: {
            onError?: () => void;
            resetJobStateOnError?: boolean;
        },
    ): (fn: () => Promise<T>) => Promise<T> {
        const errorMessage = `Failed to ${label}`;

        return async (fn: () => Promise<T>): Promise<T> => {
            // Capture the previous state BEFORE executing fn() if reset is requested
            const currentJob = this.autoLayoutStoreService.getCurrentJob();
            const previousState = options?.resetJobStateOnError && currentJob?.state ? currentJob.state : undefined;

            try {
                return await fn();
            } catch (error) {
                this.logger.error(errorMessage, {error});
                this.emit("error.occurred", {error, message: errorMessage});

                // Reset job state to previous state if requested and we have a job
                if (options?.resetJobStateOnError && currentJob && previousState !== undefined) {
                    this.autoLayoutStoreService.setState(previousState);
                }

                options?.onError?.();
                throw error;
            }
        };
    }

    /**
     * Sets up all subscriptions for handling job updates and status transitions
     * @param jobUid - The job UID to subscribe to
     */
    private setupSubscriptions(jobUid: AutoLayoutJobUid, userHandle: string): void {
        // Clear any existing subscriptions first
        this.cleanupSubscriptions();

        // Subscribe to job state changes
        try {
            let previousJob: AutoLayoutJob | undefined;

            this.jobSubscription = this.autoLayoutJobRepository.subscribe(userHandle, jobUid, (event) => {
                if (event.type === "updated") {
                    this.handleJobStateChange({
                        job: event.data,
                        previousJob: previousJob,
                    });
                    previousJob = event.data;
                }
            });

            this.logger.debug("Subscribed to job updates", {jobUid});
        } catch (error) {
            this.logger.error("Failed to subscribe to job updates", {error, jobUid});
            throw error;
        }

        // Subscribe to job metadata changes
        try {
            this.metadataSubscription = this.autoLayoutJobMetadataRepository.subscribe(userHandle, jobUid, (event) => {
                if (event.type === "updated") {
                    this.handleJobMetadataChange({metadata: event.data});
                }
            });

            this.logger.debug("Subscribed to job metadata updates", {jobUid});
        } catch (error) {
            this.logger.error("Failed to subscribe to job metadata updates", {error, jobUid});
            throw error;
        }

        // Set up event subscriptions
        this.subscriptions = [
            // 1. Reset the auto-layout review when a new job starts
            this.eventBus.subscribe("job.starting", () => this.reviewService.reset()),

            // 2. Once board is exported, run the auto-layout review checks
            this.eventBus.subscribe("board.export.completed", (data) => {
                const {exportTimeErrors, objectIdToNodeUidMap} = data.metadata;
                this.reviewService.evaluate(objectIdToNodeUidMap, exportTimeErrors);
            }),

            // 3. Handle iteration received analytics
            this.eventBus.subscribe("iteration.received", () => {
                const currentJob = this.autoLayoutStoreService.getCurrentJob();
                if (!currentJob) {
                    this.logger.warn("No current job to handle iteration received");
                    return;
                }

                trackAutoLayoutEvent(this.analyticsStorage, "new_iteration", currentJob);
            }),
        ];

        this.logger.debug("All auto-layout subscriptions set up", {
            jobUid,
            eventSubscriptionCount: this.subscriptions.length,
        });
    }

    /**
     * Cleans up all subscriptions (job, metadata, and event)
     */
    private cleanupSubscriptions(): void {
        // Clean up job and metadata subscriptions
        this.jobSubscription?.();
        this.jobSubscription = undefined;

        this.metadataSubscription?.();
        this.metadataSubscription = undefined;

        // Clean up event subscriptions
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        this.subscriptions.forEach((unsub) => unsub());
        this.subscriptions = [];

        this.logger.debug("All auto-layout subscriptions cleaned up");
    }

    /**
     * Handles job sync events from the coordinator
     * @param data - The job sync event data containing backend job and client job states
     */
    private handleJobUpdates(backendJob: AutoLayoutJob): Promise<void> {
        const runTask = this.createTaskRunner("handle job updates");

        return runTask(async () => {
            this.logger.info("Handling job updated", {jobUid: backendJob.jobUid});

            switch (backendJob.jobStatus) {
                case AutoLayoutStatus.allocated:
                case AutoLayoutStatus.submitted:
                case AutoLayoutStatus.ready:
                case AutoLayoutStatus.booting: {
                    return;
                }

                case AutoLayoutStatus.populated: {
                    this.logger.info("Sending submit request for job");

                    const document = this.documentStore.getState().document;
                    const baseDocument = this.useDocumentMetaStore.getState().baseDocument;
                    const ownerHandle = document?.owner_handle;
                    const projectSlug = document?.slug || baseDocument?.slug;
                    const projectPath = ownerHandle && projectSlug ? `${ownerHandle}/${projectSlug}` : undefined;

                    await this.apiClient.submitJob({
                        jobUid: backendJob.jobUid,
                        userUid: backendJob.submittedByUserUid,
                        inputBoardLocation: backendJob.inputBoardLocation,
                        projectPath,
                    });
                    return;
                }

                case AutoLayoutStatus.discarded: {
                    this.emit("job.canceled", {jobUid: backendJob.jobUid});
                    this.cleanup({clearIteration: true});
                    return;
                }

                case AutoLayoutStatus.applied: {
                    this.emit("job.applied", {jobUid: backendJob.jobUid});
                    this.cleanup({clearIteration: true});
                    return;
                }

                case AutoLayoutStatus.failed: {
                    this.emit("job.failed", {
                        jobUid: backendJob.jobUid,
                        error: new Error(backendJob.reason),
                    });
                    this.emit("error.occurred", {
                        error: new Error(backendJob.reason),
                        message: backendJob.reason,
                    });
                    this.cleanup({clearIteration: true});
                    return;
                }

                case AutoLayoutStatus.converged:
                case AutoLayoutStatus.timeout: {
                    const currentJob = this.autoLayoutStoreService.getCurrentJob();
                    trackAutoLayoutEvent(this.analyticsStorage, backendJob.jobStatus, currentJob);
                }
            }

            await this.iterationProcessor.processIterations(backendJob);
        });
    }

    /**
     * Handles job state changes from repository subscription
     */
    private handleJobStateChange(data: {job: AutoLayoutJob; previousJob: AutoLayoutJob | undefined}): void {
        const {job, previousJob} = data;

        if (isTrivialJobChange(previousJob, job)) {
            this.logger.debug("Ignoring trivial job change", {jobUid: job.jobUid});
            return;
        }

        this.logger.debug("Handling job state change", {
            jobUid: job.jobUid,
            status: job.jobStatus,
        });

        // Sync backend job state to store service
        this.autoLayoutStoreService.syncFromBackend(job);

        // Handle job updates directly since we have the context
        this.handleJobUpdates(job).catch((error) => {
            this.logger.error("Failed to handle job updates", {error, jobUid: job.jobUid});
        });

        // Emit job synced event for other subscribers
        this.emit("job.synced", {backendJob: job});
    }

    /**
     * Handles job metadata changes from repository subscription
     */
    private handleJobMetadataChange(data: {metadata: AutoLayoutJobMetadata}): void {
        const {metadata} = data;

        this.logger.debug("Handling job metadata change", {
            creditsUsed: metadata.creditsUsed,
        });

        const currentJob = this.autoLayoutStoreService.getCurrentJob();
        if (!currentJob?.jobUid) {
            this.logger.warn("Cannot handle metadata change: no active job");
            return;
        }

        // Update store service with credits
        const previousCredits = this.autoLayoutStoreService.getCreditsUsed();
        this.autoLayoutStoreService.updateCreditsUsed(metadata.creditsUsed);

        // Emit credits updated event if credits changed
        if (metadata.creditsUsed !== previousCredits) {
            this.emit("credits.updated", {
                jobUid: currentJob.jobUid,
                creditsUsed: metadata.creditsUsed,
            });
        }

        // Update store service with errors
        if (metadata.errors) {
            this.autoLayoutStoreService.setMetadataErrors(metadata.errors);
        }

        // Emit metadata updated event
        this.emit("metadata.updated", {
            jobUid: currentJob.jobUid,
            metadata,
        });
    }

    /**
     * Classifies nets using AI and updates their properties in the document.
     */
    private async classifyAndUpdateNetTypes(): Promise<boolean> {
        // Check if classification is enabled
        if (!this.useFeatureFlagsStore.getState().autoDetermineNetType) {
            this.logger.debug("Net classification is disabled by feature flags");
            return false;
        }

        const document = this.documentStore.getState().document;
        if (!document) {
            this.logger.error("Document not found in document store");
            return false;
        }

        try {
            this.logger.info("Starting automatic net classification");

            const netCount = Object.keys(document.nets).length;

            // Get current user for request metadata
            const currentUser = this.currentUserService.getCurrentUser();
            if (!currentUser) {
                this.logger.error("Current user not found for net classification");
                return false;
            }

            // Emit classification started event
            this.emit("netClassification.started", {projectUid: document.uid});

            // Call the classification API with documentUid
            const netTypes = await this.apiClient.classifyNets({
                documentUid: document.uid,
                requestMetaData: {
                    messageOwnerUid: currentUser.uid,
                    messageOwnerHandle: currentUser.handle,
                    projectUid: document.uid,
                    messageOwnerIsFluxOrgMember: false,
                    environment: "production",
                },
            });

            this.logger.info("Net classification completed", {
                classifiedCount: Object.keys(netTypes).length,
            });

            // Update net properties in the document
            const propertyUpdatesMap = buildNetPropertyUpdates(document.nets, netTypes);

            const updateCount = Object.keys(propertyUpdatesMap).length;
            if (updateCount === 0) {
                this.logger.debug("No net properties to update");
            } else {
                this.logger.debug("Updating net properties", {updateCount});
                await this.actionBakeObserver.asyncDispatch((subscriptionKey) =>
                    this.documentStore.getState().setSubjectProperties(propertyUpdatesMap, subscriptionKey),
                );
                this.logger.info("Net properties updated successfully");
            }

            // Emit classification completed event
            this.emit("netClassification.completed", {
                projectUid: document.uid,
                netCount,
                classificationsApplied: updateCount,
            });

            return updateCount > 0;
        } catch (error) {
            this.logger.error("Failed to classify nets", {error});

            // Emit classification failed event
            this.emit("netClassification.failed", {projectUid: document.uid, error});

            // Don't throw - net classification is optional enhancement
            return false;
        }
    }
}
